import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useUpdateCollectionCase,
  mapPriorityToId,
  mapStatusToId,
  mapRiskLevelToId,
} from '@/hooks/useCollectionCasesData';
import type { CollectionCase } from '@/types/collections';

interface UpdateNextActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseData: CollectionCase;
  onUpdated: () => void;
}

export function UpdateNextActionDialog({
  open,
  onOpenChange,
  caseData,
  onUpdated,
}: UpdateNextActionDialogProps) {
  const [nextAction, setNextAction] = useState('');
  const [nextActionDate, setNextActionDate] = useState<Date>();
  const updateCaseMutation = useUpdateCollectionCase();

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setNextAction('');
      setNextActionDate(undefined);
    }
    onOpenChange(newOpen);
  };

  const handleSkip = () => {
    handleOpenChange(false);
  };

  const handleSave = () => {
    if (!nextAction) {
      toast.error('Please select a next action');
      return;
    }
    if (!nextActionDate) {
      toast.error('Please select a due date');
      return;
    }

    updateCaseMutation.mutate(
      {
        caseId: Number(caseData.id),
        priorityId: mapPriorityToId(caseData.priority),
        statusId: mapStatusToId(caseData.status),
        strategyId: caseData.strategy?.id ? Number(caseData.strategy.id) : undefined,
        amount: caseData.amount,
        nextAction,
        nextActionDate: format(nextActionDate, 'yyyy-MM-dd'),
        riskLevelId: caseData.riskLevel ? mapRiskLevelToId(caseData.riskLevel) : undefined,
        contactId: caseData.contactId ? Number(caseData.contactId) : undefined,
        notes: caseData.notes,
      },
      {
        onSuccess: () => {
          toast.success('Next action updated successfully');
          onUpdated();
          handleOpenChange(false);
        },
        onError: () => {
          toast.error('Failed to update next action');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Next Action</DialogTitle>
          <DialogDescription>
            What would you like to do next for this case?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="update-next-action">Next Action</Label>
            <Select value={nextAction} onValueChange={setNextAction}>
              <SelectTrigger id="update-next-action">
                <SelectValue placeholder="Select next action..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                <SelectItem value="Send email">Send email</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !nextActionDate && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {nextActionDate ? format(nextActionDate, 'PPP') : 'Select a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={nextActionDate}
                  onSelect={setNextActionDate}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleSkip} disabled={updateCaseMutation.isPending}>
            Skip
          </Button>
          <Button onClick={handleSave} disabled={updateCaseMutation.isPending}>
            {updateCaseMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
